// Costo de servir por cuenta (§14), en horas del equipo al mes, y el onboarding de cada negocio nuevo: de registro
// a primera solución en uso sin intervención de ingeniería, que es el criterio de salida de la Fase 4.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, TarjetaIndicador, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { fechaCorta, momento } from '../lib/formato';
import { useSesion } from '../lib/sesion';

export function Costo() {
  const qc = useQueryClient();
  const { aviso } = useSesion();
  const q = useQuery({ queryKey: ['panel-costo'], queryFn: () => api<any>('/api/panel/costo') });
  const accesos = useQuery({ queryKey: ['panel-soporte'], queryFn: () => api<any[]>('/api/panel/soporte') });
  const cuentas = useQuery({ queryKey: ['panel'], queryFn: () => api<any>('/api/panel') });
  const [anotar, setAnotar] = useState(false);
  const [f, setF] = useState({ cuentaId: '', fecha: new Date().toISOString().slice(0, 10), minutos: '30', tipo: 'ingesta', nota: '' });

  const guardar = useMutation({
    mutationFn: () => api('/api/panel/tiempo', { cuerpo: { ...f, minutos: Number(f.minutos), nota: f.nota || undefined } }),
    onSuccess: () => { aviso('Anotado.'); setAnotar(false); void qc.invalidateQueries({ queryKey: ['panel-costo'] }); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (q.isLoading || !q.data) return <Cargando />;
  const { meses, porCuenta, onboarding } = q.data;

  return (
    <div className="page">
      <PageHead title="Costo de servir" subtitle="Horas del equipo por cuenta al mes, y cuánto tarda un negocio nuevo en llegar a su primera solución en uso." />
      <div className="kpis">
        <TarjetaIndicador
          label="Horas del equipo por cuenta"
          value={meses[0] ? `${meses[0].horasPorCuenta} h` : '—'}
          tone={meses[0] ? (meses[0].horasPorCuenta <= 2 ? 'ok' : 'pending') : 'neutral'}
          trend={meses[0] ? `${meses[0].mes} · ${meses[0].cuentas} negocios atendidos` : 'Sin tiempo anotado todavía'}
        />
        <TarjetaIndicador
          label="Llegaron solos a su primer dato"
          value={onboarding.pct === null ? '—' : `${onboarding.pct}%`}
          tone={onboarding.pct === null ? 'neutral' : onboarding.pct >= 50 ? 'ok' : 'pending'}
          trend="De registro a primera solución en uso, sin que el equipo meta la mano"
        />
        <TarjetaIndicador label="Accesos de soporte" value={accesos.data?.length ?? 0} trend="Últimos 60 días · cada uno queda en la bitácora del negocio" />
      </div>
      <div style={{ marginBottom: 14 }}><Button icon="plus" onClick={() => setAnotar(true)}>Anotar tiempo</Button></div>

      <div className="eyebrow">Por mes</div>
      <Tabla rowKey="mes" rows={meses}
        columns={[
          { key: 'mes', label: 'Mes' },
          { key: 'cuentas', label: 'Negocios atendidos', numeric: true },
          { key: 'minutos', label: 'Minutos del equipo', numeric: true },
          { key: 'horasPorCuenta', label: 'Horas por cuenta', numeric: true, render: (m: any) => <Badge state={m.horasPorCuenta <= 2 ? 'ok' : 'pending'}>{`${m.horasPorCuenta} h`}</Badge> },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Por negocio</div>
      {!porCuenta.length ? <Vacio titulo="Sin tiempo anotado">El soporte se anota solo al cerrar el acceso; lo demás se anota a mano.</Vacio> : (
        <Tabla rowKey="k" rows={porCuenta.map((c: any, i: number) => ({ ...c, k: i }))}
          columns={[
            { key: 'mes', label: 'Mes' },
            { key: 'negocio', label: 'Negocio' },
            { key: 'minutos', label: 'Minutos', numeric: true },
            { key: 'tipos', label: 'En qué', render: (c: any) => Object.entries(c.tipos).map(([t, m]) => `${t} ${m} min`).join(' · ') },
          ]} />
      )}

      <div className="eyebrow" style={{ marginTop: 18 }}>De registro a primera solución en uso</div>
      <Tabla rowKey="cuentaId" rows={onboarding.casos}
        columns={[
          { key: 'nombre', label: 'Negocio' },
          { key: 'creada_en', label: 'Se registró', render: (c: any) => fechaCorta(String(c.creada_en)) },
          { key: 'primer_dato', label: 'Primer dato propio', render: (c: any) => (c.primer_dato ? fechaCorta(String(c.primer_dato)) : <span className="muted">Todavía no</span>) },
          { key: 'minutos', label: 'Cuánto tardó', render: (c: any) => (c.minutos === null ? '—' : c.minutos < 120 ? `${c.minutos} min` : `${Math.round(c.minutos / 1440)} días`) },
          { key: 'soloEllos', label: 'Sin el equipo', render: (c: any) => <Badge state={c.soloEllos ? 'ok' : 'pending'}>{c.soloEllos ? 'Solos' : `${c.toquesEquipo} veces el equipo`}</Badge> },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Accesos de soporte</div>
      {!accesos.data?.length ? <Vacio titulo="Sin accesos">Nadie del equipo ha entrado a la cuenta de un cliente.</Vacio> : (
        <Tabla rowKey="id" rows={accesos.data}
          columns={[
            { key: 'negocio', label: 'Negocio' },
            { key: 'nombre', label: 'Quién' },
            { key: 'motivo', label: 'Motivo' },
            { key: 'abierto_en', label: 'Abrió', render: (a: any) => momento(a.abierto_en) },
            { key: 'cerrado_en', label: 'Estado', render: (a: any) => (a.cerrado_en ? <Badge state="neutral">Cerrado</Badge> : new Date(a.expira_en) > new Date() ? <Badge state="pending">Abierto</Badge> : <Badge state="neutral">Vencido</Badge>) },
            { key: 'consultas', label: 'Consultas', numeric: true },
          ]} />
      )}

      {anotar && (
        <Modal title="Anotar tiempo del equipo" subtitle="Lo que no pasó por un acceso de soporte: una llamada, un arreglo de ingesta, un acompañamiento." onClose={() => setAnotar(false)}
          footer={<Button onClick={() => guardar.mutate()} disabled={guardar.isPending || !f.cuentaId}>Anotar</Button>}>
          <Select label="Negocio" value={f.cuentaId} options={[{ value: '', label: 'Elegir' }, ...(cuentas.data?.cuentas ?? []).map((c: any) => ({ value: c.id, label: c.nombre }))]} onChange={(e) => setF({ ...f, cuentaId: e.target.value })} />
          <Input label="Fecha" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} type="date" />
          <Input label="Minutos" value={f.minutos} onChange={(e) => setF({ ...f, minutos: e.target.value })} inputMode="numeric" />
          <Select label="En qué" value={f.tipo} options={[
            { value: 'soporte', label: 'Soporte' }, { value: 'ingesta', label: 'Ingesta' }, { value: 'onboarding', label: 'Onboarding' },
            { value: 'construccion', label: 'Construir con el negocio' }, { value: 'otro', label: 'Otro' },
          ]} onChange={(e) => setF({ ...f, tipo: e.target.value })} />
          <Input label="Nota" value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} />
        </Modal>
      )}
    </div>
  );
}
